/** What the activity's cards show for a field beyond its stored value as text (plan §3): the
 *  read-only values from the detail payload (the Google place and its stored data, the ratings
 *  over their sources, the zone, the provenance, the legacy texts), the custom ID in mono, and each linkable
 *  value's actions — the grids it filters, the map, the phone, the mail, the web. Pure over
 *  the detail. */
import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import type { ActivityDetail, ActivityPlace } from '@/api/activities';
import type { ActivityRecordFieldKey } from '@/api/activityRecord';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { MONO_SX } from '@/features/bookings/record/panels/displays';
import {
  activityCategoryActions,
  activityCountryActions,
  activityCurrencyActions,
  activityCustomIdActions,
  activityPriceTypeActions,
  activityTypeActions,
  addressActions,
  latLngActions,
  webUrlActions,
  type CatalogCtx,
} from '@/features/catalog/catalogLinks';
import { catalogSourceLabel } from '@/features/catalog/words';
import { formatZoned } from '@/utils/zonedTime';

import { activityMapHref, ratingText } from '../parts/activityFactBandModel';
import { activityTypeLabel } from '../parts/activityNames';

export interface ActivityDisplayContext {
  detail: ActivityDetail;
  links: CatalogCtx;
  now?: Date;
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const utc = (ts: string | null | undefined) => (ts ? formatZoned(ts, 'UTC', { weekday: false }) : null);

function mono(text: string, actions: ValueAction[]): ReactNode {
  return (
    <ValueActions actions={actions}>
      <Box component="span" sx={MONO_SX}>
        {text}
      </Box>
    </ValueActions>
  );
}

/** The linked Google place: the place on Google Maps (the detail's own link) and its id. */
export function googlePlaceActions(place: ActivityPlace): ValueAction[] {
  return [act.external(place.link, 'Open the place in Google Maps'), act.copy(place.id, 'Copy place id')];
}

/** The address's actions: the activity on the map (`activityMapHref` — its Google place,
 *  else its coordinates), else a search for the address; and the address itself. */
export function addressMapActions(
  address: string,
  a: Pick<ActivityDetail, 'place' | 'latitude' | 'longitude'>,
): ValueAction[] {
  const href = activityMapHref(a);
  return href ? [act.external(href, 'Open in Google Maps'), act.copy(address, 'Copy address')] : addressActions(address);
}

/** "14 fields" — what the stored Google place data holds, by count; null when none. */
export function placeDataSummary(data: Record<string, unknown> | null): string | null {
  if (!data) return null;
  const n = Object.keys(data).length;
  return n ? plural(n, 'field') : null;
}

/** A field's rendering in place of its stored value as text — every read-only field's, and
 *  the custom ID's (mono); undefined = the value as text. */
export function activityFieldDisplay(key: ActivityRecordFieldKey, c: ActivityDisplayContext): ReactNode | undefined {
  const a = c.detail;
  switch (key) {
    case 'custom_id': {
      const v = a.custom_id?.trim();
      return v ? mono(v, activityCustomIdActions(v, c.links)) : null;
    }
    case 'place':
      return a.place ? mono(a.place.id, googlePlaceActions(a.place)) : 'Not linked to a Google place';
    case 'timezone':
      return a.timezone?.trim() || null;
    case 'ratings':
      return ratingText(a.usage.ratings, a.rating_count) ?? 'No ratings';
    case 'nearby_of':
      return a.usage.nearby_of ? plural(a.usage.nearby_of, 'activity', 'activities') : 'None';
    case 'source':
      return catalogSourceLabel(a.source, c.now);
    case 'ext_id': {
      const v = a.ext_id?.trim();
      return v ? mono(v, [act.copy(v, 'Copy import identity')]) : null;
    }
    case 'created_ts':
      return utc(a.created_ts);
    case 'updated_ts':
      return utc(a.updated_ts);
    case 'place_data': {
      const summary = placeDataSummary(a.place_data);
      return summary && a.place_data ? (
        <ValueActions actions={[act.copy(JSON.stringify(a.place_data, null, 2), 'Copy the place data (JSON)')]}>
          {summary}
        </ValueActions>
      ) : null;
    }
    case 'version':
      return String(a.version);
    case 'notes':
    case 'ontrip_description':
    case 'description_abbrev':
      return a[key]?.trim() || null;
    default:
      return undefined;
  }
}

/** A linkable editable value's actions, from its stored value; undefined = none. */
export function activityFieldActions(
  key: ActivityRecordFieldKey,
  c: ActivityDisplayContext,
): ((value: unknown) => ValueAction[]) | undefined {
  const a = c.detail;
  switch (key) {
    case 'name':
      return (v) => (typeof v === 'string' ? [act.copy(v, 'Copy name')] : []);
    case 'type':
      return () => activityTypeActions(a.type, activityTypeLabel(a), c.links);
    case 'category':
      return () => (a.category ? activityCategoryActions(a.category, c.links) : []);
    case 'address':
      return (v) => (typeof v === 'string' ? addressMapActions(v, a) : []);
    case 'coordinates':
      return (v) => {
        const p = v as { latitude?: unknown; longitude?: unknown } | null;
        return p && typeof p.latitude === 'number' && typeof p.longitude === 'number'
          ? latLngActions({ lat: p.latitude, lng: p.longitude })
          : [];
      };
    case 'country':
      return () => (a.country ? activityCountryActions(a.country, c.links) : []);
    case 'currency':
      return () => (a.currency ? activityCurrencyActions(a.currency.code, c.links) : []);
    case 'price_type':
      return () => (a.price_type_display ? activityPriceTypeActions(a.price_type_display, c.links) : []);
    case 'directions_url':
    case 'website':
    case 'ext_link':
      return (v) => webUrlActions(typeof v === 'string' ? v : null);
    case 'phone':
      return (v) => (typeof v === 'string' ? [act.tel(v), act.copy(v, 'Copy phone')] : []);
    case 'email':
      return (v) => (typeof v === 'string' ? [act.mailto(v), act.copy(v, 'Copy email')] : []);
    default:
      return undefined;
  }
}
